
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService.ts';
import { trackEvent } from '../services/analyticsService.ts';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'signin' | 'signup';
  reason?: string;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialMode = 'signup', reason }) => {
  const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowConfirmation(false);
      setEmail('');
      setPassword('');
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    setShowConfirmation(false);
  }, [mode]);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (mode === 'signup') {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        trackEvent('auth_signup_success', { email });
        setShowConfirmation(true);
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        trackEvent('auth_signin_success', { email });
        onClose();
      }
    } catch (err: any) {
      setError(err.message);
      trackEvent('auth_error', { mode, error: err.message });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-indigo-950/60 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl p-10 sm:p-14 relative animate-in zoom-in duration-300 border border-slate-100">
        <button onClick={onClose} className="absolute top-10 right-10 p-2 text-slate-300 hover:text-slate-900 transition-colors">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        <div className="text-center">
          {showConfirmation ? (
            <>
              <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-8 text-3xl font-bold">✉️</div>
              <h3 className="text-3xl font-black text-slate-900 mb-4 tracking-tight">Check Your Email</h3>
              <p className="text-slate-500 mb-8 text-sm leading-relaxed font-medium">We've sent a secure link to <span className="text-indigo-600 font-bold">{email}</span>. Click it to activate your bridge.</p>
              <button 
                onClick={onClose}
                className="w-full bg-indigo-900 text-white py-5 rounded-2xl font-black text-lg hover:bg-indigo-950 transition-all shadow-xl shadow-indigo-100"
              >
                Got it
              </button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-slate-50 text-indigo-900 rounded-2xl flex items-center justify-center mx-auto mb-8 text-2xl font-black">H</div>
              <h3 className="text-3xl font-black text-slate-900 mb-2 tracking-tight">
                {mode === 'signup' ? 'Join the Bridge' : 'Welcome Back'}
              </h3>
              <p className="text-slate-500 mb-10 text-sm leading-relaxed font-medium">
                {reason || (mode === 'signup' ? "Start reclaiming your evenings today." : "Pick up where you left off.")}
              </p>

              {error && <div className="mb-8 p-4 bg-red-50 text-red-600 rounded-2xl text-[11px] font-black uppercase tracking-widest">{error}</div>}

              <form onSubmit={handleAuth} className="space-y-4">
                <input 
                  type="email" 
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email Address" 
                  className="w-full px-7 py-5 rounded-2xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-8 focus:ring-indigo-50 focus:border-indigo-200 transition-all font-semibold text-sm" 
                />
                <input 
                  type="password" 
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create Password" 
                  className="w-full px-7 py-5 rounded-2xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-8 focus:ring-indigo-50 focus:border-indigo-200 transition-all font-semibold text-sm" 
                />
                <button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-indigo-900 text-white py-5 rounded-2xl font-black text-lg hover:bg-indigo-950 transition-all shadow-xl shadow-indigo-100 active:scale-95 disabled:opacity-50"
                >
                  {loading ? 'Processing...' : (mode === 'signup' ? 'Create Account' : 'Sign In')}
                </button>
              </form>

              <div className="mt-10 text-sm font-semibold">
                <span className="text-slate-400">{mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}</span>
                <button onClick={() => setMode(mode === 'signup' ? 'signin' : 'signup')} className="ml-2 text-indigo-600 font-black hover:underline underline-offset-4">
                  {mode === 'signup' ? 'Sign In' : 'Sign Up'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
